/**
 * ProofreadHint — vérif orthographe/grammaire silencieuse sous un champ texte.
 *
 *   <FormTextarea name="story_fr" ... />
 *   <ProofreadHint
 *     name="story_fr"
 *     onOpenInModal={(text) => openCorrectionFromHint('story_fr', 'Story', text)}
 *   />
 *
 * Comportement :
 *  - Watch la valeur du champ via RHF `useWatch`.
 *  - Quand la valeur dépasse `MIN_CHARS` ET reste stable pendant
 *    `DEBOUNCE_MS` ET diffère de la dernière valeur vérifiée, lance un appel
 *    `POST /api/llm/proofread`. Le debounce reset à chaque frappe — ce qui
 *    revient en pratique à un on-blur (l'utilisateur arrête de taper quand
 *    il sort du champ).
 *  - 3 affichages possibles :
 *    - "Vérification orthographe…" (pendant le call)
 *    - "✓ Pas de faute détectée" (passive)
 *    - "✏️ Corrections suggérées — voir" (cliquable, ouvre la modale
 *      Aide-moi pré-remplie via `onOpenInModal`)
 *
 * Le composant n'ouvre pas de modale lui-même — il délègue. La modale
 * "Aide-moi" existe déjà dans NewWorkPage avec ses 4 cartes (Corrigé +
 * 3 reformulations) ; l'utilisateur clique le hint, NewWorkPage ouvre la
 * modale en court-circuit (la correction est déjà connue, pas de 2e appel
 * Claude).
 *
 * État dérivé du hook TanStack Mutation (`proofread.variables` + `data`)
 * — pas de `useState` redondant, pas de set-state-in-effect.
 */

import { useEffect } from 'react'
import { useFormContext, useWatch, type FieldPath, type FieldValues } from 'react-hook-form'

import Button from '@/components/ui/Button'
import { useProofreadField } from '@/hooks/useTranslate'

import './ProofreadHint.css'

/** Seuil minimum pour déclencher proofread — aligné avec PROOFREAD_MIN_CHARS BE. */
const MIN_CHARS = 10
/** Délai de stabilité avant déclenchement (~équivalent on-blur). */
const DEBOUNCE_MS = 2500

interface ProofreadHintProps<TFieldValues extends FieldValues> {
  name: FieldPath<TFieldValues>
  onOpenInModal: (correctedText: string) => void
  /**
   * Notifié quand l'état "des corrections sont suggérées" change. Permet au
   * parent de teinter visuellement le champ sous-jacent (fond rouge subtil)
   * pour que l'utilisateur ne loupe pas le hint inline qui, lui seul,
   * passait inaperçu en bas du textarea.
   */
  onHasCorrectionsChange?: (hasCorrections: boolean) => void
  /**
   * Mode compact pour rendu sur la ligne du label (slot `labelActions`
   * de `<Field>`). Supprime les margins de haut.
   */
  inline?: boolean
}

export default function ProofreadHint<TFieldValues extends FieldValues = FieldValues>({
  name,
  onOpenInModal,
  onHasCorrectionsChange,
  inline = false,
}: ProofreadHintProps<TFieldValues>) {
  const { control } = useFormContext<TFieldValues>()
  const value = useWatch({ control, name })
  const text = typeof value === 'string' ? value : ''

  const proofread = useProofreadField()
  const lastChecked = proofread.variables?.raw_input ?? null

  useEffect(() => {
    if (text.length < MIN_CHARS) return
    if (text === lastChecked) return

    const timeoutId = window.setTimeout(() => {
      proofread.mutate({ raw_input: text })
    }, DEBOUNCE_MS)

    return () => window.clearTimeout(timeoutId)
    // proofread.mutate est stable (mutation TanStack) — exclu volontairement
    // des deps pour éviter une boucle de re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, lastChecked])

  const hasCorrections =
    text.length >= MIN_CHARS && lastChecked === text && Boolean(proofread.data?.has_changes)

  useEffect(() => {
    onHasCorrectionsChange?.(hasCorrections)
  }, [hasCorrections, onHasCorrectionsChange])

  if (text.length < MIN_CHARS) return null

  const hintClass = inline ? 'proofread-hint proofread-hint--inline' : 'proofread-hint'
  const buttonClass = inline
    ? 'proofread-hint-fix-button proofread-hint-fix-button--inline'
    : 'proofread-hint-fix-button'

  // Texte en cours de vérification : on n'affiche le pending que si le
  // mutate concerne bien le texte courant (sinon c'est une vérif obsolète).
  if (proofread.isPending && lastChecked === text) {
    return <p className={`${hintClass} proofread-hint-pending`}>Vérification orthographe…</p>
  }

  // Pas encore de résultat pour ce texte (en attente du debounce, ou
  // résultat précédent obsolète) : silencieux.
  if (!proofread.data || lastChecked !== text) return null

  if (!proofread.data.has_changes) {
    return <p className={`${hintClass} proofread-hint-ok`}>✓ Pas de faute détectée</p>
  }

  return (
    <Button
      variant="quiet"
      size="sm"
      type="button"
      className={buttonClass}
      onClick={() => onOpenInModal(proofread.data.correction)}
    >
      ✏️ Corrections suggérées — voir
    </Button>
  )
}
